import { styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { cloneDeep } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import useFormValidation from 'services/hooks/useFormValidation';
import {
    Box,
    Button,
    Divider,
    Form,
    Icon,
    IconButton,
    MenuItem,
    ResponsiveDialog,
    Select,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from 'shell/components';
import { v4 as uuid } from 'uuid';

const FIELD_TYPE_OPTIONS = [
    { key: 'single-select', value: 'Single Select' },
    { key: 'multi-select', value: 'Multi-select' },
    { key: 'likert-scale', value: 'Likert Scale' },
    { key: 'rank', value: 'Rank' },
];

const getDefaultForm = (criterion) => {
    return criterion
        ? {
              name: criterion.name,
              importance: criterion.weight,
              question: criterion.custom_question,
              field_type: criterion.field_type,
              options: criterion.options || [],
              mixture: criterion.mixture,
          }
        : { name: '', importance: 3, question: '', field_type: 'single-select', options: [], mixture: 'homogeneous' };
};

const getHelperText = (fieldType) => {
    return {
        'single-select': 'Select one option from a pre-defined list.',
        'multi-select': 'Select multiple options from a pre-defined list.',
        'likert-scale': 'Select an option from a pre-determined scale.',
        rank: 'Give a numerical rank to a given list of items.',
    }[fieldType];
};

const AddEditCriterionDialog = ({
    groupFormation,
    surveys,
    criterion,
    weightOptions,
    mixtureOptions,
    open,
    onClose,
    onDelete,
    onSubmit,
}) => {
    const { device } = useViewport();

    const listEndRef = useRef(null);

    const [form, setForm] = useState(getDefaultForm(criterion));
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [isAddingNewOption, setIsAddingNewOption] = useState(false);
    const [newOption, setNewOption] = useState('');

    const mode = criterion ? 'edit' : 'add';
    const isDefaultCriterion = ['time-zone', 'country', 'gender', 'english-proficiency'].includes(criterion?.id);
    const isPublished = Boolean(groupFormation?.status === 'published' && surveys.length);
    const isDisabled = isDefaultCriterion || isPublished;

    const { errors, validate, validateAll } = useFormValidation(form, (key, value) => {
        if (['name'].includes(key)) value = value?.trim();
        switch (key) {
            case 'name':
                return value ? '' : 'Please enter a criterion name';
            case 'options':
                return isDefaultCriterion || value.length ? '' : 'Please add an option';
            default:
                return '';
        }
    });

    const hasError = Boolean(Object.values(errors).reduce((acc, cur) => cur + acc, ''));

    const removeOption = (option) => {
        setForm((current) => {
            const newOptions = current.options.filter(({ key }) => key !== option.key);
            if (hasClickedSubmit || errors.options) {
                validate('options', newOptions);
            }
            return {
                ...current,
                options: newOptions,
            };
        });
    };

    const addNewOption = (newOption) => {
        setForm((current) => {
            const newOptions = [...(current.options || []), { key: uuid(), value: newOption }];
            if (hasClickedSubmit || errors.options) {
                validate('options', newOptions);
            }
            return {
                ...current,
                options: newOptions,
            };
        });
        setNewOption('');
    };

    const handleBlur = () => {
        if (newOption.trim()) {
            addNewOption(newOption);
        }
        setIsAddingNewOption(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            setIsAddingNewOption(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (newOption.trim()) {
                addNewOption(newOption);
            }
        }
    };

    const handleChange = (key, value) => {
        setForm((current) => ({ ...current, [key]: value }));
        if (hasClickedSubmit || errors[key]) {
            validate(key, value);
        }
    };

    const handleDragEnd = ({ source, destination, draggableId }) => {
        const srcIndex = source.index;
        const dstIndex = destination.index;
        if (srcIndex === dstIndex) return;
        setForm((current) => {
            const newOptions = cloneDeep(current.options);
            newOptions.splice(srcIndex, 1);
            newOptions.splice(
                dstIndex,
                0,
                current.options.find(({ key }) => key === draggableId)
            );
            return { ...current, options: newOptions };
        });
    };

    const handleSubmit = () => {
        if (!hasClickedSubmit) setHasClickedSubmit(true);
        if (!validateAll()) return;
        onSubmit({
            id: criterion?.id,
            name: form.name,
            custom_question: form.question,
            weight: form.importance,
            field_type: form.field_type,
            mixture: form.mixture,
            options: form.options,
        });
    };

    useEffect(() => {
        if (listEndRef.current) {
            listEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [form.options]);

    useEffect(() => {
        if (open) setForm(getDefaultForm(criterion));
    }, [open, criterion]);

    return (
        <CustomResponsiveDialog device={device} open={open} onClose={onClose}>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Text variant={'h6'}>{mode === 'add' ? 'Add' : 'Edit'} Criterion</Text>
                <IconButton kind={'exit'} size={'medium'} variant={'square'} onClick={onClose} />
            </Stack>
            {isDisabled && (
                <Stack sx={{ my: 2 }} alignItems={'center'} direction={'row'} spacing={2}>
                    <CustomIcon kind={'error'} />
                    <Text variant={'p3'}>
                        {isPublished
                            ? 'Some fields have been disabled as students have started taking the course intake form.'
                            : 'Editing is limited as this criteria is automatically answered when a student sets up their profile.'}
                    </Text>
                </Stack>
            )}
            <Form>
                <Stack divider={<Divider />} spacing={5}>
                    <Stack spacing={5}>
                        <Stack alignItems={'flex-end'} direction={device === 'mobile' ? 'column' : 'row'} spacing={5}>
                            <TextInput
                                disabled={isDisabled}
                                error={Boolean(errors.name)}
                                helperText={errors.name}
                                label={'Name'}
                                placeholder={'Enter Criterion Name'}
                                required
                                value={form.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                            />
                            <Select
                                label={
                                    <Stack alignItems={'flex-end'} direction={'row'} spacing={2}>
                                        <Text variant={'p2'}>Importance</Text>
                                        <Tooltip
                                            content={
                                                'Importance allows the algorithm to determine which criteria to prioritize when forming groups.'
                                            }
                                            type={'info'}
                                        />
                                    </Stack>
                                }
                                labelId={'criterion-importance'}
                                value={form.importance}
                                onChange={(e) => handleChange('importance', e.target.value)}
                            >
                                {weightOptions.map((option) => (
                                    <MenuItem key={option.key} value={option.key}>
                                        {option.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                        <TextInput
                            disabled={isDisabled}
                            label={'Question'}
                            placeholder={'Enter a Question'}
                            value={form.question}
                            onChange={(e) => handleChange('question', e.target.value)}
                        />
                    </Stack>
                    <Stack spacing={2}>
                        <Stack spacing={1}>
                            <Select
                                sx={device !== 'mobile' && { width: 'calc(50% - 10px)' }}
                                disabled={isDisabled}
                                label={'Field Type'}
                                labelId={'criterion-field-type'}
                                value={form.field_type}
                                onChange={(e) => handleChange('field_type', e.target.value)}
                            >
                                {FIELD_TYPE_OPTIONS.map((option) => (
                                    <MenuItem key={option.key} value={option.key}>
                                        {option.value}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Text color={'light'} variant={'p3'}>
                                {getHelperText(form.field_type)}
                            </Text>
                        </Stack>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable
                                droppableId={'criterion-options-droppable'}
                                isDropDisabled={isDisabled}
                                spacing={1}
                            >
                                {(provided) => (
                                    <CustomStack ref={provided.innerRef} {...provided.droppableProps} spacing={2}>
                                        {form.options.map((option, index) => (
                                            <Draggable
                                                key={option.key}
                                                draggableId={option.key}
                                                index={index}
                                                isDragDisabled={isDisabled}
                                            >
                                                {(provided) => (
                                                    <Stack
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        alignItems={'center'}
                                                        direction={'row'}
                                                        justifyContent={'space-between'}
                                                    >
                                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                                            <Icon
                                                                sx={{
                                                                    ...(isDisabled && {
                                                                        color: (theme) =>
                                                                            theme.palette.ensightful.neutral[400],
                                                                    }),
                                                                }}
                                                                kind={'reorder'}
                                                            />
                                                            <Text variant={'p2'}>
                                                                {form.field_type === 'likert-scale' &&
                                                                    `${index + 1} - `}
                                                                {option.value}
                                                            </Text>
                                                        </Stack>
                                                        <IconButton
                                                            disabled={isDisabled}
                                                            kind={'exit'}
                                                            size={'small'}
                                                            variant={'square'}
                                                            onClick={() => removeOption(option)}
                                                        />
                                                    </Stack>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                        <Box ref={listEndRef} />
                                    </CustomStack>
                                )}
                            </Droppable>
                        </DragDropContext>
                        {isAddingNewOption && (
                            <Stack>
                                <TextInput
                                    autoFocus
                                    placeholder={'Enter input option'}
                                    value={newOption}
                                    onBlur={handleBlur}
                                    onChange={(e) => setNewOption(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    onKeyPress={handleKeyPress}
                                />
                            </Stack>
                        )}
                        <Button
                            sx={{ alignSelf: 'flex-start' }}
                            disabled={isDisabled}
                            startIcon={<Icon kind={'add'} />}
                            variant={'tertiary'}
                            onClick={() => setIsAddingNewOption(true)}
                        >
                            Add Option
                        </Button>
                        <Text color={'error'} variant={'p3'}>
                            {errors.options}
                        </Text>
                    </Stack>
                    <Stack>
                        <Select
                            sx={device !== 'mobile' && { width: 'calc(50% - 10px)' }}
                            label={'Place students with the same answer in...'}
                            labelId={'criterion-mixture'}
                            value={form.mixture}
                            onChange={(e) => handleChange('mixture', e.target.value)}
                        >
                            {mixtureOptions.map((option) => (
                                <MenuItem key={option.key} value={option.key}>
                                    {option.value}
                                </MenuItem>
                            ))}
                        </Select>
                    </Stack>
                </Stack>
            </Form>
            <FooterStack direction={'row'} device={device} justifyContent={'space-between'}>
                <Button
                    color={'neutral'}
                    disabled={isDisabled}
                    startIcon={<Icon kind={'delete'} />}
                    variant={'tertiary'}
                    onClick={onDelete}
                >
                    Delete
                </Button>
                <Stack direction={'row'} spacing={2}>
                    <Button color={'neutral'} variant={'tertiary'} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={hasError} onClick={handleSubmit}>
                        {mode === 'add' ? 'Create' : 'Update'} Criterion
                    </Button>
                </Stack>
            </FooterStack>
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(({ device }) => {
    return {
        ...(device !== 'mobile' && {
            '& .MuiDialog-paper': {
                width: '40rem',
            },
        }),
    };
});

const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        borderRadius: '50%',
        padding: '0.25rem',
        fontSize: '1rem',
        background: theme.palette.ensightful.error[700],
        color: theme.palette.ensightful.shades[0],
    };
});

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(1, 0, -1),
        maxHeight: 208,
        overflowY: 'auto',
    };
});

const FooterStack = styled(Stack)(({ device, theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        margin: theme.spacing(4, -8, -8),
        padding: theme.spacing(4, 6, 5),
        ...(device === 'mobile' && {
            margin: theme.spacing(4, -5, 0),
            padding: theme.spacing(4, 5, 5),
        }),
    };
});

export default AddEditCriterionDialog;
